/**
 * adminStaffStore — Staff slice of the admin store (Zustand).
 * Exposes ONLY the staff endpoints (`/api/admin/staff`).
 *
 * NOTE: Resource persons are a separate concept and live in
 * `adminResourcePersonStore.js` (`/api/admin/resource-persons`).
 */
import { authHeaders, resolveToken } from "./adminUtils";
import { toDisplayString } from "../../utils/toDisplayString";
import { API_URL } from "../../constants";

export const staffInitialState = {
  // List
  staff: [],
  staffLoading: false,
  staffError: null,
  staffTotal: 0,
  staffPages: 1,

  // Single staff member
  staffDetail: null,
  staffDetailLoading: false,
  staffDetailError: null,

  // Activity log
  staffActivity: [],
  staffActivityLoading: false,
  staffActivityError: null,
  staffActivityTotal: 0,
  staffActivityPages: 1,

  // Mutation state
  staffMutationLoading: false,
  staffMutationError: null,
};

/** Normalize any list payload shape into `{ items, total, totalPages }`. */
function normalizeListPayload(data) {
  const payload = data?.data || data || {};
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, totalPages: 1 };
  }
  return {
    items: payload.items || payload.staff || [],
    total: payload.total ?? payload.totalItems ?? 0,
    totalPages: payload.totalPages ?? payload.pages ?? 1,
  };
}

/**
 * Thin authenticated JSON fetch. Reads the response as text first so a
 * successful empty body (e.g. `204 No Content` from a DELETE) never throws a
 * JSON parse error, then surfaces the API error envelope as a thrown Error.
 */
async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const looksJson =
      contentType.includes("application/json") ||
      text.trim().startsWith("{") ||
      text.trim().startsWith("[");
    if (looksJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(
      toDisplayString(data?.error ?? data?.message, `Request failed (${response.status})`)
    );
  }

  return data;
}

export function createStaffActions(set, get) {
  return {
    // ── GET /api/admin/staff ─────────────────────────────────────────────────
    fetchStaff: async (params = {}) => {
      set({ staffLoading: true, staffError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const data = await apiRequest(get, `/api/admin/staff${query ? `?${query}` : ""}`);
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          staffLoading: false,
          staff: items,
          staffTotal: total,
          staffPages: totalPages,
        });
        return { success: true, staff: items, total, pages: totalPages };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch staff");
        set({ staffLoading: false, staffError: message, staff: [] });
        return { success: false, error: message };
      }
    },

    // ── POST /api/admin/staff ────────────────────────────────────────────────
    createStaff: async (payload) => {
      set({ staffMutationLoading: true, staffMutationError: null });
      try {
        const data = await apiRequest(get, "/api/admin/staff", {
          method: "POST",
          body: payload,
        });
        set({ staffMutationLoading: false, successMessage: "Staff created" });
        return { success: true, staffMember: data.data || data };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to create staff");
        set({ staffMutationLoading: false, staffMutationError: message });
        return { success: false, error: message };
      }
    },

    // ── GET /api/admin/staff/:id ─────────────────────────────────────────────
    fetchStaffMember: async (id) => {
      set({ staffDetailLoading: true, staffDetailError: null });
      try {
        const data = await apiRequest(get, `/api/admin/staff/${id}`);
        const staffMember = data.data || data;
        set({ staffDetailLoading: false, staffDetail: staffMember });
        return { success: true, staffMember };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch staff member");
        set({ staffDetailLoading: false, staffDetailError: message });
        return { success: false, error: message };
      }
    },

    // ── PATCH /api/admin/staff/:id ───────────────────────────────────────────
    updateStaff: async (id, payload) => {
      set({ staffMutationLoading: true, staffMutationError: null });
      try {
        const data = await apiRequest(get, `/api/admin/staff/${id}`, {
          method: "PATCH",
          body: payload,
        });
        set({ staffMutationLoading: false, successMessage: "Staff updated" });
        return { success: true, staffMember: data.data || data };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to update staff");
        set({ staffMutationLoading: false, staffMutationError: message });
        return { success: false, error: message };
      }
    },

    // ── PATCH /api/admin/staff/:id/status ────────────────────────────────────
    updateStaffStatus: async (id, status) => {
      set({ staffMutationLoading: true, staffMutationError: null });
      try {
        await apiRequest(get, `/api/admin/staff/${id}/status`, {
          method: "PATCH",
          body: { status },
        });
        set({ staffMutationLoading: false, successMessage: "Status updated" });
        return { success: true };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to update status");
        set({ staffMutationLoading: false, staffMutationError: message });
        return { success: false, error: message };
      }
    },

    // ── DELETE /api/admin/staff/:id ──────────────────────────────────────────
    deleteStaff: async (id) => {
      set({ staffMutationLoading: true, staffMutationError: null });
      try {
        // A successful DELETE is often 204/empty — apiRequest handles that.
        await apiRequest(get, `/api/admin/staff/${id}`, { method: "DELETE" });
        set({ staffMutationLoading: false, successMessage: "Staff deleted" });
        return { success: true };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to delete staff");
        set({ staffMutationLoading: false, staffMutationError: message });
        return { success: false, error: message };
      }
    },

    // ── GET /api/admin/staff/:id/activity ────────────────────────────────────
    fetchStaffActivity: async (id, params = {}) => {
      set({ staffActivityLoading: true, staffActivityError: null });
      try {
        const query = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
        ).toString();
        const data = await apiRequest(
          get,
          `/api/admin/staff/${id}/activity${query ? `?${query}` : ""}`
        );
        const { items, total, totalPages } = normalizeListPayload(data);
        set({
          staffActivityLoading: false,
          staffActivity: items,
          staffActivityTotal: total,
          staffActivityPages: totalPages,
        });
        return { success: true, activity: items, total, pages: totalPages };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch staff activity");
        set({ staffActivityLoading: false, staffActivityError: message });
        return { success: false, error: message };
      }
    },
  };
}

const staffActions = createStaffActions;
export default staffActions;